/**
 * EKGRenderer handles the real-time drawing of a medical cardiac pulse.
 * It uses Canvas for high performance.
 */
export class EKGRenderer {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.width = canvas.width;
        this.height = canvas.height;
        
        this.points = [];
        this.x = 0;
        this.bpm = 80;
        this.isCritical = false;
        this.lastTime = 0;
        this.pulsePhase = 0;
        
        // Aesthetic settings
        this.lineColor = '#00ff41'; // Matrix/Medical Green
        this.glowColor = 'rgba(0, 255, 65, 0.5)';
        this.lineWidth = 2.5;
        this.onPeak = null; // Callback for synchronized sound
        this.hasTriggeredPeak = false;
    }

    setBPM(bpm, isCritical = false) {
        this.bpm = bpm;
        this.isCritical = isCritical;
    }

    /**
     * Resizes canvas to match its CSS dimensions.
     */
    resize() {
        const rect = this.canvas.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
        this.width = rect.width;
        this.height = rect.height;
    }

    /**
     * Generates a single point of the EKG wave based on the current phase.
     * Follows the P-QRS-T sequence.
     */
    _getPulseY(phase) {
        const mid = this.height / 2;
        
        // P Wave (Atrial Depolarization)
        if (phase > 0.1 && phase < 0.2) {
            return mid - Math.sin((phase - 0.1) * Math.PI * 10) * 5;
        }
        
        // QRS Complex (Ventricular Depolarization)
        if (phase > 0.3 && phase < 0.35) return mid + 10; // Q
        if (phase >= 0.35 && phase < 0.4) {
            // R Peak
            const p = (phase - 0.35) * 20; // 0 to 1
            return mid - 40 * Math.sin(p * Math.PI);
        }
        if (phase >= 0.4 && phase < 0.45) return mid + 15; // S
        
        // T Wave (Ventricular Repolarization)
        if (phase > 0.6 && phase < 0.8) {
            return mid - Math.sin((phase - 0.6) * Math.PI * 5) * 8;
        }
        
        return mid; // Baseline
    }

    update(timestamp) {
        if (!this.lastTime) this.lastTime = timestamp;
        const deltaTime = timestamp - this.lastTime;
        this.lastTime = timestamp;

        // Advance pulse phase based on BPM
        // BPM 60 = 1 pulse per second = phase 1.0 per 1000ms
        const phaseIncrement = (this.bpm / 60) * (deltaTime / 1000);
        this.pulsePhase = (this.pulsePhase + phaseIncrement) % 1.0;

        const speed = this.width / 3; // Traverse screen in 3 seconds
        const dx = speed * (deltaTime / 1000);
        this.x += dx;

        // Trigger peak event for sound synchronization (around R peak phase 0.35)
        if (this.pulsePhase >= 0.35 && !this.hasTriggeredPeak) {
            if (this.onPeak) this.onPeak(this.isCritical);
            this.hasTriggeredPeak = true;
        }
        if (this.pulsePhase < 0.3) {
            this.hasTriggeredPeak = false; // Reset for next cycle
        }

        const y = this._getPulseY(this.pulsePhase);
        
        this.points.push({ x: this.x, y: y });

        // Remove points that are off-screen
        while (this.points.length > 0 && this.points[0].x < this.x - this.width) {
            this.points.shift();
        }

        this.draw();
    }

    draw() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.width, this.height);

        if (this.points.length < 2) return;

        ctx.save();
        
        // Medical Glow Effect
        ctx.shadowBlur = 8;
        ctx.shadowColor = this.glowColor;
        ctx.strokeStyle = this.lineColor;
        ctx.lineWidth = this.lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.beginPath();
        
        // Drawing points with X wrap-around logic
        // We draw relative to the current X so it feels like it's scrolling
        for (let i = 0; i < this.points.length; i++) {
            const p = this.points[i];
            const screenX = this.width - (this.x - p.x);
            
            if (i === 0) ctx.moveTo(screenX, p.y);
            else ctx.lineTo(screenX, p.y);
        }
        
        ctx.stroke();
        
        // Head of the line (current pulse)
        const lastPoint = this.points[this.points.length - 1];
        ctx.beginPath();
        ctx.arc(this.width, lastPoint.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();
        
        ctx.restore();
    }
}
